import React, { useState, useEffect } from 'react';
import { Bell, Mail, Clock, Save, RefreshCw } from 'lucide-react';
import { getApiUrl } from '../../config/api';
import keycloak from '../../config/keycloak';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

/**
 * Per-user notification preferences. Works for any role.
 *
 * Usage: <NotificationPreferences currentUser={currentUser} />
 * If no user is passed, the logged-in Keycloak user (token subject) is used.
 * The server only lets a user read/write their own preferences.
 */
interface NotificationPreferencesProps {
  currentUser?: { id: string; name?: string } | null;
  /** @deprecated pass `currentUser` instead */
  userId?: string;
  /** @deprecated unused; accepted for backward compatibility */
  userName?: string;
}

// Only the preference keys the server actually honors are shown/saved.
// Push opt-in is the browser push subscription (Push Notifications screen), not a preference here.
const EMAIL_GROUPS: { title: string; border: string; items: { key: string; label: string }[] }[] = [
  {
    title: 'Deployment Events',
    border: 'border-blue-200',
    items: [
      { key: 'deploymentCreated', label: 'Deployment Created' },
      { key: 'deploymentApproved', label: 'Deployment Approved' },
      { key: 'deploymentRejected', label: 'Deployment Rejected' },
      { key: 'deploymentDeployed', label: 'Deployment Deployed' }
    ]
  },
  {
    title: 'Approval Events',
    border: 'border-yellow-200',
    items: [
      { key: 'approvalPending', label: 'Approval Pending (New)' },
      { key: 'approvalReminder', label: 'Approval Reminder' }
    ]
  },
  {
    title: 'Task Events',
    border: 'border-green-200',
    items: [
      { key: 'taskAssigned', label: 'Task Assigned to Me' },
      { key: 'taskDueSoon', label: 'Task Due Soon' },
      { key: 'taskOverdue', label: 'Task Overdue' },
      { key: 'taskCompleted', label: 'Task Completed' }
    ]
  },
  {
    title: 'Slot Events',
    border: 'border-purple-200',
    items: [
      { key: 'slotBooked', label: 'Slot Booked' },
      { key: 'slotCancelled', label: 'Slot Cancelled' },
      { key: 'slotRejected', label: 'Slot Rejected' }
    ]
  }
];

const EMAIL_KEYS = ['enabled', ...EMAIL_GROUPS.flatMap(g => g.items.map(i => i.key))];

interface Preferences {
  emailNotifications: Record<string, boolean>;
  quietHours: { enabled: boolean; startTime: string; endTime: string };
}

const normalize = (data: any): Preferences => {
  const email: Record<string, boolean> = {};
  EMAIL_KEYS.forEach(key => {
    email[key] = data?.emailNotifications?.[key] ?? true;
  });
  return {
    emailNotifications: email,
    quietHours: {
      enabled: data?.quietHours?.enabled ?? false,
      startTime: data?.quietHours?.startTime || '22:00',
      endTime: data?.quietHours?.endTime || '08:00'
    }
  };
};

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ currentUser, userId: legacyUserId }) => {
  const userId = currentUser?.id || legacyUserId || keycloak.subject || '';
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) fetchPreferences();
    else {
      setLoading(false);
      setLoadError('Could not determine the logged-in user.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await fetch(getApiUrl(`/api/user-preferences/${encodeURIComponent(userId)}`));
      if (!response.ok) {
        const message = await readApiError(response, 'Failed to load preferences');
        setLoadError(message);
        toast(message, 'error');
        return;
      }
      const data = await response.json();
      setPreferences(normalize(data));
    } catch (error) {
      console.error('Error fetching preferences:', error);
      setLoadError('Failed to load preferences');
      toast('Failed to load preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!preferences) return;
    try {
      setSaving(true);
      // Send only the honored keys; the server deep-merges them into the stored document
      const response = await fetch(getApiUrl(`/api/user-preferences/${encodeURIComponent(userId)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      });
      if (!response.ok) {
        toast(await readApiError(response, 'Failed to save preferences'), 'error');
        return;
      }
      toast('Preferences saved', 'success');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast('Failed to save preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  const setEmail = (key: string, value: boolean) =>
    setPreferences(prev => prev && ({ ...prev, emailNotifications: { ...prev.emailNotifications, [key]: value } }));

  const setQuietHours = (patch: Partial<Preferences['quietHours']>) =>
    setPreferences(prev => prev && ({ ...prev, quietHours: { ...prev.quietHours, ...patch } }));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading preferences...</span>
      </div>
    );
  }

  if (!preferences) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600 mb-3">{loadError || 'Failed to load preferences.'}</p>
        {userId && (
          <button
            onClick={fetchPreferences}
            className="px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  const emailEnabled = preferences.emailNotifications.enabled;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Notification Preferences</h2>
                <p className="text-sm text-gray-600">Manage how you receive notifications</p>
              </div>
            </div>

            <button
              onClick={savePreferences}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Email Notifications */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Email Notifications</h3>
            </div>

            <div className="space-y-3 pl-7">
              {/* Master Toggle */}
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <span className="font-medium text-gray-900">Enable Email Notifications</span>
                  <p className="text-sm text-gray-500">Master switch for all email notifications</p>
                </div>
                <input
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={(e) => setEmail('enabled', e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
              </label>

              {EMAIL_GROUPS.map(group => (
                <div key={group.title} className={`space-y-2 pl-4 border-l-2 ${group.border}`}>
                  <h4 className="font-medium text-gray-700 text-sm uppercase tracking-wide">{group.title}</h4>
                  {group.items.map(item => (
                    <label key={item.key} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <span className={`text-sm ${emailEnabled ? 'text-gray-700' : 'text-gray-400'}`}>{item.label}</span>
                      <input
                        type="checkbox"
                        checked={preferences.emailNotifications[item.key]}
                        onChange={(e) => setEmail(item.key, e.target.checked)}
                        disabled={!emailEnabled}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </section>


          {/* Quiet Hours */}
          <section className="pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Quiet Hours</h3>
            </div>

            <div className="space-y-3 pl-7">
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <span className="font-medium text-gray-900">Enable Quiet Hours</span>
                  <p className="text-sm text-gray-500">No push notifications during these hours (IST)</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.quietHours.enabled}
                  onChange={(e) => setQuietHours({ enabled: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
              </label>

              {preferences.quietHours.enabled && (
                <>
                <div className="grid grid-cols-2 gap-4 pl-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                    <input
                      type="time"
                      value={preferences.quietHours.startTime}
                      onChange={(e) => setQuietHours({ startTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                    <input
                      type="time"
                      value={preferences.quietHours.endTime}
                      onChange={(e) => setQuietHours({ endTime: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 pl-4">
                  {preferences.quietHours.startTime === preferences.quietHours.endTime
                    ? 'Start and end are the same, so quiet hours have no effect.'
                    : preferences.quietHours.startTime > preferences.quietHours.endTime
                      ? `Spans midnight: quiet from ${preferences.quietHours.startTime} until ${preferences.quietHours.endTime} the next morning.`
                      : `Quiet from ${preferences.quietHours.startTime} to ${preferences.quietHours.endTime} the same day.`}
                </p>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;
